import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, ChevronDown, ChevronUp, AlertTriangle, RefreshCw, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../../../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../../../components/brand/BrandTypography';
import { API_URL } from '../constants';

const DisclaimerTab = () => {
    const [disclaimer, setDisclaimer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedSection, setExpandedSection] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);

    // Fetch disclaimer content
    useEffect(() => {
        fetchDisclaimer();
    }, []);

    const fetchDisclaimer = async () => {
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/admin/disclaimer`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setDisclaimer(data);
            } else {
                // If no disclaimer exists, create default structure
                setDisclaimer(getDefaultDisclaimer());
            }
        } catch (error) {
            console.error('Error fetching disclaimer:', error);
            setDisclaimer(getDefaultDisclaimer());
        } finally {
            setLoading(false);
        }
    };

    const getDefaultDisclaimer = () => ({
        version: '1.0',
        title: 'Before You Begin',
        subtitle: 'Please read and acknowledge the following',
        importantNotice: {
            title: 'Important Notice',
            content: 'CoRegulate is designed to support your emotional wellbeing, but it is not a substitute for professional mental health care. Please read each section carefully.'
        },
        sections: [
            {
                id: 'general',
                title: 'General Disclaimer',
                icon: 'Heart',
                content: [
                    {
                        heading: 'Professional Development',
                        text: 'The tools, techniques, and content within CoRegulate have been professionally developed and designed by a qualified Clinical Hypnotherapist with expertise in ADHD and emotional regulation.'
                    },
                    {
                        heading: 'Not a Replacement for Therapy',
                        text: 'While our tools are evidence-based and professionally crafted, they are intended for educational and self-help purposes only. CoRegulate is not intended to replace, substitute, or serve as an alternative to:',
                        list: [
                            'Professional therapeutic services',
                            'Psychological counselling or psychotherapy',
                            'Psychiatric care or medication',
                            'Medical advice or treatment',
                            'Crisis intervention services'
                        ]
                    },
                    {
                        heading: 'Seek Professional Help',
                        text: 'If you are experiencing a mental health crisis, severe emotional distress, suicidal thoughts, or any condition requiring professional intervention, please contact a qualified mental health professional, your healthcare provider, or emergency services immediately.'
                    },
                    {
                        heading: 'Individual Results May Vary',
                        text: 'The effectiveness of self-regulation tools varies between individuals. What works well for one person may not work the same way for another.'
                    }
                ]
            },
            {
                id: 'trance',
                title: 'Trance States & Relaxation Techniques',
                icon: 'Brain',
                content: [
                    {
                        heading: 'Nature of Our Tools',
                        text: 'Some tools within CoRegulate incorporate elements of:',
                        list: [
                            'Guided meditation and visualisation',
                            'Light hypnotherapy techniques',
                            'Breathing exercises that may induce deep relaxation',
                            'Somatic experiencing practices',
                            'Body scan and progressive relaxation'
                        ]
                    },
                    {
                        heading: 'Possible Effects',
                        text: 'These practices may naturally induce light trance states, deep relaxation, or altered states of awareness. This is a normal and safe response to relaxation techniques.'
                    },
                    {
                        heading: 'Safe Use Guidelines',
                        text: '',
                        list: [
                            'Do not use these tools while driving, operating machinery, or in situations requiring full alertness',
                            'Ensure you are in a safe, comfortable environment before beginning any guided practice',
                            'If you have a history of dissociative disorders, PTSD, or trauma, please consult with your mental health provider before using deep relaxation tools',
                            'You can stop any practice at any time if you feel uncomfortable'
                        ]
                    },
                    {
                        heading: 'Contraindications',
                        text: 'If you have epilepsy, a seizure disorder, or any condition that may be affected by deep relaxation or rhythmic stimuli, please consult your healthcare provider before use.'
                    }
                ]
            },
            {
                id: 'privacy',
                title: 'Privacy & Data Protection',
                icon: 'Lock',
                content: [
                    {
                        heading: 'Your Privacy Matters',
                        text: 'We are committed to protecting your personal information and maintaining your privacy.'
                    },
                    {
                        heading: 'Data We Collect',
                        text: '',
                        list: [
                            'Account information (name, email)',
                            'Usage patterns and tool preferences',
                            'Progress and session history',
                            'Saved favourites and personalisation settings'
                        ]
                    },
                    {
                        heading: 'How We Use Your Data',
                        text: '',
                        list: [
                            'To personalise your experience and recommendations',
                            'To track your progress and provide insights',
                            'To improve our tools and services',
                            'To communicate important updates about the service'
                        ]
                    },
                    {
                        heading: 'Data Security',
                        text: 'Your data is encrypted and stored securely. We do not sell, rent, or share your personal information with third parties for marketing purposes.'
                    },
                    {
                        heading: 'Your Rights',
                        text: 'You have the right to access, correct, or delete your personal data at any time. You can manage your data through your account settings or by contacting us.'
                    },
                    {
                        heading: 'Confidentiality',
                        text: 'Any information you share through the app, including emotional states, relationship context, and personal reflections, is treated as confidential and is never shared externally.'
                    }
                ]
            }
        ],
        acknowledgments: [
            {
                id: 'notTherapy',
                text: 'I understand that CoRegulate is not a substitute for professional therapy or medical treatment, and I will seek appropriate professional help when needed.'
            },
            {
                id: 'tranceStates',
                text: 'I acknowledge that some tools may induce relaxation, light trance states, or altered awareness, and I will use them in safe environments.'
            },
            {
                id: 'privacy',
                text: 'I have read and accept the privacy policy and consent to the collection and use of my data as described.'
            }
        ],
        buttons: {
            decline: "I'll Come Back Later",
            accept: 'I Understand & Accept',
            acceptDisabled: 'Please Acknowledge All'
        }
    });

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/admin/disclaimer`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(disclaimer)
            });
            
            if (response.ok) {
                toast.success('Disclaimer saved successfully');
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            console.error('Error saving disclaimer:', error);
            toast.error('Failed to save disclaimer');
        } finally {
            setSaving(false);
        }
    };

    const updateSection = (sectionIndex, contentIndex, field, value) => {
        const updated = { ...disclaimer };
        updated.sections[sectionIndex].content[contentIndex][field] = value;
        setDisclaimer(updated);
    };

    const updateSectionTitle = (sectionIndex, value) => {
        const updated = { ...disclaimer };
        updated.sections[sectionIndex].title = value;
        setDisclaimer(updated);
    };

    const updateListItem = (sectionIndex, contentIndex, listIndex, value) => {
        const updated = { ...disclaimer };
        updated.sections[sectionIndex].content[contentIndex].list[listIndex] = value;
        setDisclaimer(updated);
    };

    const addListItem = (sectionIndex, contentIndex) => {
        const updated = { ...disclaimer };
        if (!updated.sections[sectionIndex].content[contentIndex].list) {
            updated.sections[sectionIndex].content[contentIndex].list = [];
        }
        updated.sections[sectionIndex].content[contentIndex].list.push('New item');
        setDisclaimer(updated);
    };

    const removeListItem = (sectionIndex, contentIndex, listIndex) => {
        const updated = { ...disclaimer };
        updated.sections[sectionIndex].content[contentIndex].list.splice(listIndex, 1);
        setDisclaimer(updated);
    };

    const updateAcknowledgment = (index, value) => {
        const updated = { ...disclaimer };
        updated.acknowledgments[index].text = value;
        setDisclaimer(updated);
    };

    const addContentBlock = (sectionIndex) => {
        const updated = { ...disclaimer };
        updated.sections[sectionIndex].content.push({
            heading: 'New Heading',
            text: 'New content text'
        });
        setDisclaimer(updated);
    };

    const removeContentBlock = (sectionIndex, contentIndex) => {
        const updated = { ...disclaimer };
        updated.sections[sectionIndex].content.splice(contentIndex, 1);
        setDisclaimer(updated);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-[#1D6B6B]" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <Heading level={2} className="text-[#1D6B6B]">Disclaimer Editor</Heading>
                    <SmallText className="text-[#7D8E95]">
                        Edit the sign-up disclaimer content shown to new users
                    </SmallText>
                </div>
                <div className="flex items-center gap-3">
                    <SecondaryButton onClick={() => setPreviewMode(!previewMode)}>
                        <Eye className="w-4 h-4" />
                        {previewMode ? 'Edit Mode' : 'Preview'}
                    </SecondaryButton>
                    <PrimaryButton onClick={handleSave} disabled={saving}>
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </PrimaryButton>
                </div>
            </div>

            {/* Version Info */}
            <div className="glass-card-sage rounded-xl p-4 flex items-center justify-between">
                <div>
                    <SmallText className="text-[#1D6B6B] font-medium">Current Version</SmallText>
                    <BodyText className="text-[#3A3A37]">{disclaimer.version}</BodyText>
                </div>
                <div>
                    <label className="text-sm text-[#7D8E95]">Update Version</label>
                    <input
                        type="text"
                        value={disclaimer.version}
                        onChange={(e) => setDisclaimer({ ...disclaimer, version: e.target.value })}
                        className="ml-2 px-3 py-1 rounded-lg border border-[#FFBB98]/30 bg-white/60 text-sm w-20"
                    />
                </div>
            </div>

            {/* Title & Subtitle */}
            <div className="glass-card rounded-xl p-6 space-y-4">
                <Heading level={3} className="text-[#1D6B6B]">Header Content</Heading>
                
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm text-[#7D8E95] mb-1">Title</label>
                        <input
                            type="text"
                            value={disclaimer.title}
                            onChange={(e) => setDisclaimer({ ...disclaimer, title: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-[#7D8E95] mb-1">Subtitle</label>
                        <input
                            type="text"
                            value={disclaimer.subtitle}
                            onChange={(e) => setDisclaimer({ ...disclaimer, subtitle: e.target.value })}
                            className="w-full px-4 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                        />
                    </div>
                </div>
            </div>

            {/* Important Notice */}
            <div className="glass-card-rose rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-[#E08850]" />
                    <Heading level={3} className="text-[#1D6B6B]">Important Notice Banner</Heading>
                </div>
                
                <div className="space-y-3">
                    <div>
                        <label className="block text-sm text-[#7D8E95] mb-1">Notice Title</label>
                        <input
                            type="text"
                            value={disclaimer.importantNotice.title}
                            onChange={(e) => setDisclaimer({ 
                                ...disclaimer, 
                                importantNotice: { ...disclaimer.importantNotice, title: e.target.value }
                            })}
                            className="w-full px-4 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-[#7D8E95] mb-1">Notice Content</label>
                        <textarea
                            value={disclaimer.importantNotice.content}
                            onChange={(e) => setDisclaimer({ 
                                ...disclaimer, 
                                importantNotice: { ...disclaimer.importantNotice, content: e.target.value }
                            })}
                            rows={3}
                            className="w-full px-4 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                        />
                    </div>
                </div>
            </div>

            {/* Sections */}
            <div className="space-y-4">
                <Heading level={3} className="text-[#1D6B6B]">Disclaimer Sections</Heading>
                
                {disclaimer.sections.map((section, sectionIndex) => (
                    <div key={section.id} className="glass-card rounded-xl overflow-hidden">
                        <button
                            onClick={() => setExpandedSection(expandedSection === sectionIndex ? null : sectionIndex)}
                            className="w-full flex items-center justify-between p-4 bg-white/40 hover:bg-white/60 transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <span className="text-lg font-medium text-[#1D6B6B]">{section.title}</span>
                                <span className="text-xs text-[#7D8E95] bg-[#7D8E95]/10 px-2 py-0.5 rounded">
                                    {section.content.length} blocks
                                </span>
                            </div>
                            {expandedSection === sectionIndex ? (
                                <ChevronUp className="w-5 h-5 text-[#7D8E95]" />
                            ) : (
                                <ChevronDown className="w-5 h-5 text-[#7D8E95]" />
                            )}
                        </button>
                        
                        {expandedSection === sectionIndex && (
                            <div className="p-4 space-y-4 border-t border-[#FFBB98]/30">
                                <div>
                                    <label className="block text-sm text-[#7D8E95] mb-1">Section Title</label>
                                    <input
                                        type="text"
                                        value={section.title}
                                        onChange={(e) => updateSectionTitle(sectionIndex, e.target.value)}
                                        className="w-full px-4 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                                    />
                                </div>
                                
                                {section.content.map((block, contentIndex) => (
                                    <div key={contentIndex} className="bg-white/40 rounded-xl p-4 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-medium text-[#1D6B6B]">
                                                Content Block {contentIndex + 1}
                                            </label>
                                            <TertiaryButton 
                                                onClick={() => removeContentBlock(sectionIndex, contentIndex)}
                                                className="text-red-500 hover:text-red-600"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </TertiaryButton>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-xs text-[#7D8E95] mb-1">Heading</label>
                                            <input
                                                type="text"
                                                value={block.heading}
                                                onChange={(e) => updateSection(sectionIndex, contentIndex, 'heading', e.target.value)}
                                                className="w-full px-3 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60 text-sm"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-xs text-[#7D8E95] mb-1">Text</label>
                                            <textarea
                                                value={block.text}
                                                onChange={(e) => updateSection(sectionIndex, contentIndex, 'text', e.target.value)}
                                                rows={2}
                                                className="w-full px-3 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60 text-sm"
                                            />
                                        </div>
                                        
                                        {block.list && (
                                            <div>
                                                <label className="block text-xs text-[#7D8E95] mb-1">List Items</label>
                                                <div className="space-y-2">
                                                    {block.list.map((item, listIndex) => (
                                                        <div key={listIndex} className="flex items-center gap-2">
                                                            <span className="text-[#7D8E95]">•</span>
                                                            <input
                                                                type="text"
                                                                value={item}
                                                                onChange={(e) => updateListItem(sectionIndex, contentIndex, listIndex, e.target.value)}
                                                                className="flex-1 px-3 py-1.5 rounded-lg border border-[#FFBB98]/30 bg-white/60 text-sm"
                                                            />
                                                            <button
                                                                onClick={() => removeListItem(sectionIndex, contentIndex, listIndex)}
                                                                className="text-red-400 hover:text-red-500"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        onClick={() => addListItem(sectionIndex, contentIndex)}
                                                        className="flex items-center gap-1 text-sm text-[#1D6B6B] hover:text-[#145555]"
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                        Add list item
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {!block.list && (
                                            <button
                                                onClick={() => {
                                                    const updated = { ...disclaimer };
                                                    updated.sections[sectionIndex].content[contentIndex].list = ['New item'];
                                                    setDisclaimer(updated);
                                                }}
                                                className="text-sm text-[#1D6B6B] hover:text-[#145555]"
                                            >
                                                + Add list to this block
                                            </button>
                                        )}
                                    </div>
                                ))}
                                
                                <button
                                    onClick={() => addContentBlock(sectionIndex)}
                                    className="flex items-center gap-2 text-sm text-[#1D6B6B] hover:text-[#145555] p-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Content Block
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Acknowledgments */}
            <div className="glass-card-lavender rounded-xl p-6 space-y-4">
                <Heading level={3} className="text-[#1D6B6B]">Acknowledgment Checkboxes</Heading>
                <SmallText className="text-[#7D8E95]">
                    These are the statements users must agree to before creating an account
                </SmallText>
                
                <div className="space-y-3">
                    {disclaimer.acknowledgments.map((ack, index) => (
                        <div key={ack.id} className="bg-white/40 rounded-xl p-4">
                            <label className="block text-xs text-[#7D8E95] mb-1">
                                Acknowledgment {index + 1} ({ack.id})
                            </label>
                            <textarea
                                value={ack.text}
                                onChange={(e) => updateAcknowledgment(index, e.target.value)}
                                rows={2}
                                className="w-full px-3 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60 text-sm"
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Button Labels */}
            <div className="glass-card rounded-xl p-6 space-y-4">
                <Heading level={3} className="text-[#1D6B6B]">Button Labels</Heading>
                
                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm text-[#7D8E95] mb-1">Decline Button</label>
                        <input
                            type="text"
                            value={disclaimer.buttons.decline}
                            onChange={(e) => setDisclaimer({ 
                                ...disclaimer, 
                                buttons: { ...disclaimer.buttons, decline: e.target.value }
                            })}
                            className="w-full px-4 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-[#7D8E95] mb-1">Accept Button</label>
                        <input
                            type="text"
                            value={disclaimer.buttons.accept}
                            onChange={(e) => setDisclaimer({ 
                                ...disclaimer, 
                                buttons: { ...disclaimer.buttons, accept: e.target.value }
                            })}
                            className="w-full px-4 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-[#7D8E95] mb-1">Disabled State</label>
                        <input
                            type="text"
                            value={disclaimer.buttons.acceptDisabled}
                            onChange={(e) => setDisclaimer({ 
                                ...disclaimer, 
                                buttons: { ...disclaimer.buttons, acceptDisabled: e.target.value }
                            })}
                            className="w-full px-4 py-2 rounded-lg border border-[#FFBB98]/30 bg-white/60"
                        />
                    </div>
                </div>
            </div>

            {/* Save Button (bottom) */}
            <div className="flex justify-end">
                <PrimaryButton onClick={handleSave} disabled={saving} size="lg">
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save All Changes'}
                </PrimaryButton>
            </div>
        </div>
    );
};

export default DisclaimerTab;
